import { useCallback, useState } from 'react';

import { Y_CAPTCHA_PARAM_KEY, YandexCaptchaError } from 'constants/captcha/yandex';

import { YandexCaptchaStatus } from 'types/captcha';
import { IdType } from 'types/common';

interface IYandexCaptchaHook {
  onChangeError: (error: string) => void;
  onSubmit: (data: unknown) => void;
}

export interface IYandexCaptchaHookResponse {
  key: IdType;
  token: string | null;
  status: YandexCaptchaStatus;
  error: string | null;
  visible: boolean;
  onChange: (token: string) => void;
  onChangeVisible: (visible: boolean) => void;
  onResetCaptcha: () => void;
  onChangeStatus: (status: YandexCaptchaStatus, error: string) => void;
  checkIsCaptchaError: (error: string) => boolean;
  onSubmit: (data: object) => void;
}

const useYandexCaptcha = ({ onChangeError, onSubmit }: IYandexCaptchaHook): IYandexCaptchaHookResponse => {
  const [key, setKey] = useState(Date.now());
  const [token, setToken] = useState<string>(null);
  const [error, setError] = useState(null);
  const [visible, setVisible] = useState(false);
  const [status, setStatus] = useState(YandexCaptchaStatus.NOT_VALIDATED);

  const onChangeStatus = useCallback(
    (status: YandexCaptchaStatus, error: string) => {
      setStatus(status);

      if (status === YandexCaptchaStatus.ERROR) {
        setError(error);
        onChangeError(error);
      } else {
        setError(null);
        onChangeError(null);
      }
    },
    [onChangeError],
  );

  const onResetCaptcha = useCallback(() => {
    setKey(Date.now());
    setToken(null);
    setError(null);
    setVisible(false);
    setStatus(YandexCaptchaStatus.NOT_VALIDATED);
  }, []);

  const checkIsCaptchaError = useCallback((error: string) => {
    return Object.values(YandexCaptchaError).includes(error);
  }, []);

  const handleSubmit = (data: object) => {
    if (status === YandexCaptchaStatus.SUCCESS) {
      return onSubmit({ ...data, [Y_CAPTCHA_PARAM_KEY]: token });
    }

    if (status === YandexCaptchaStatus.NOT_VALIDATED) {
      return setVisible(true);
    }

    onResetCaptcha();
  };

  return {
    key,
    token,
    status,
    error,
    visible,
    onChange: setToken,
    onChangeVisible: setVisible,
    onResetCaptcha,
    onChangeStatus,
    checkIsCaptchaError,
    onSubmit: handleSubmit,
  };
};

export default useYandexCaptcha;
